import { Alert, Box, Chip, CircularProgress, Container, Grid, Link, Paper, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { AtYourPortalBlock } from "@/components/VulnerabilityWatch/AtYourPortalBlock.tsx";
import { Kpi } from "@/components/VulnerabilityWatch/Info.tsx";
import { StatusChip } from "@/pages/VulnerabilityWatchPage.tsx";
import { ApiError } from "@/services/api.ts";
import { usePortalOverview, useVulnerability } from "@/hooks/useVulnerabilityWatch.ts";
import { formatDateTime, formatIsoDateTime } from "@/utils/formatDateTime.ts";
import * as copy from "@/utils/vulnerabilityCopy.ts";
import type { PortalOverview, VulnerabilityDetail, WatchReporterStatus } from "@/types";

function TimelineRow({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <Stack direction="row" spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Stack>
  );
}

function Header({ data }: { readonly data: VulnerabilityDetail }) {
  const severity = copy.severityLabel(data.severity, data.cvss);
  return (
    <>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>{data.name}</Typography>
        <Chip size="small" variant="outlined" label={data.cve} />
        {severity && <Chip size="small" variant="outlined" label={severity} />}
        <StatusChip status={data.status} />
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 3 }}>{data.short_description}</Typography>
    </>
  );
}

const STATUS_RANK: Record<WatchReporterStatus["status"], number> = { affects: 0, check: 1, fixed: 2 };

function worstReporter(reporters: readonly WatchReporterStatus[]): WatchReporterStatus | undefined {
  return reporters.reduce<WatchReporterStatus | undefined>(
    (worst, r) => (worst === undefined || STATUS_RANK[r.status] < STATUS_RANK[worst.status] ? r : worst),
    undefined,
  );
}

function ReporterTable({ reporters }: { readonly reporters: readonly WatchReporterStatus[] }) {
  return (
    <Table size="small" sx={{ mt: 2 }}>
      <TableHead>
        <TableRow>
          <TableCell>{copy.COL_REPORTED_VERSION}</TableCell>
          <TableCell>{copy.COL_STATUS}</TableCell>
          <TableCell>{copy.COL_REASON}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {reporters.map((r) => (
          <TableRow key={r.key}>
            <TableCell>{r.version ?? copy.NOT_KNOWN}</TableCell>
            <TableCell><StatusChip status={r.status} /></TableCell>
            <TableCell>{r.reason}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function Figures({ data, overview }: { readonly data: VulnerabilityDetail; readonly overview: PortalOverview | undefined }) {
  const reporter = worstReporter(data.reporters);
  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Kpi label={copy.KPI_PRODUCT}
            value={reporter ? copy.catalogueProductLabel(data.kev_product, reporter.version) : copy.NOT_KNOWN} />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Kpi label={copy.COL_STATUS} value={copy.statusLabel(data.status)} tip={data.reason} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Kpi label={copy.COL_DETECTION} value={copy.detectionLabel(data.detection)}
            tip={data.detection === "none" ? copy.TIP_NOT_VISIBLE : copy.TIP_DETECTION} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <AtYourPortalBlock cve={data.cve} detection={data.detection} overview={overview} />
        </Grid>
      </Grid>
      {data.reporters.length > 0 && <ReporterTable reporters={data.reporters} />}
    </Paper>
  );
}

function Timeline({ data }: { readonly data: VulnerabilityDetail }) {
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="overline" color="text.secondary">{copy.TIMELINE}</Typography>
      <Stack spacing={0.5}>
        {data.published && <TimelineRow label={copy.TIMELINE_PUBLISHED} value={formatIsoDateTime(data.published)} />}
        <TimelineRow label={copy.TIMELINE_LISTED} value={data.listed_on} />
        {data.first_seen_by_shield && (
          <TimelineRow label={copy.TIMELINE_FIRST_SEEN} value={formatIsoDateTime(data.first_seen_by_shield)} />
        )}
        {data.emailed_at !== null && <TimelineRow label={copy.EMAILED} value={formatDateTime(data.emailed_at)} />}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>{copy.CREDIT}</Typography>
    </Paper>
  );
}

export default function VulnerabilityPage() {
  const { cve = "" } = useParams();
  const { data, isLoading, error } = useVulnerability(cve);
  const overview = usePortalOverview();
  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}><CircularProgress /></Box>;
  if (error instanceof ApiError && error.status === 404) {
    return <Alert severity="info" sx={{ mt: 2 }}>{copy.NOT_KNOWN}</Alert>;
  }
  if (error || !data) return <Alert severity="error" sx={{ mt: 2 }}>{copy.LOAD_FAILED}</Alert>;

  return (
    <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <Header data={data} />
      <Figures data={data} overview={overview.data} />
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="overline" color="text.secondary">{copy.REQUIRED_ACTION}</Typography>
        <Typography sx={{ mb: data.vendor_link ? 2 : 0 }}>{data.required_action}</Typography>
        {data.vendor_link && (
          <Link href={data.vendor_link} target="_blank" rel="noopener noreferrer">{copy.VENDOR_ADVISORY}</Link>
        )}
      </Paper>
      <Timeline data={data} />
    </Container>
  );
}
